import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";
import type { PaginationState } from "./usePagination";

type InvoiceStatus = Database["public"]["Enums"]["invoice_status"];

// Includes a pseudo-filter "needs_review" that surfaces invoices missing
// a customer or project assignment (typically from QB imports).
export type InvoiceFilterStatus = InvoiceStatus | "all" | "needs_review";

export type InvoiceFilters = {
  customer?: string;
  amountMin?: number;
  amountMax?: number;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;   // YYYY-MM-DD
  invoiceNumber?: string;
  memo?: string;
  date?: string;     // YYYY-MM-DD exact
  type?: "qb_import" | "manual";
};

export const useInvoices = (
  statusFilter: InvoiceFilterStatus = "all",
  pagination?: PaginationState,
  search?: string,
  filters?: InvoiceFilters,
) => {
  const { user } = useAuth();
  const trimmedSearch = search?.trim() || "";
  const f = filters ?? {};
  const customer = f.customer?.trim() || "";
  const invoiceNumber = f.invoiceNumber?.trim() || "";
  const memo = f.memo?.trim() || "";
  const { amountMin, amountMax, dateFrom, dateTo, date, type } = f;

  const applyFilters = <T extends { eq: any; gte: any; lte: any; ilike: any; or: any; neq: any }>(q: T): T => {
    let out: any = q;
    if (statusFilter === "needs_review") {
      out = out.or("needs_customer_assignment.eq.true,needs_project_assignment.eq.true");
    } else if (statusFilter !== "all") {
      out = out.eq("status", statusFilter);
    }
    if (trimmedSearch) out = out.ilike("invoice_number", `%${trimmedSearch}%`);
    if (invoiceNumber) out = out.ilike("invoice_number", `%${invoiceNumber}%`);
    if (memo) out = out.ilike("notes", `%${memo}%`);
    if (customer) out = out.ilike("customers.name", `%${customer}%`);
    if (typeof amountMin === "number" && !Number.isNaN(amountMin)) out = out.gte("total", amountMin);
    if (typeof amountMax === "number" && !Number.isNaN(amountMax)) out = out.lte("total", amountMax);
    if (dateFrom) out = out.gte("invoice_date", dateFrom);
    if (dateTo) out = out.lte("invoice_date", dateTo);
    if (date) out = out.eq("invoice_date", date);
    if (type === "qb_import") out = out.eq("source", "qb_import");
    else if (type === "manual") out = out.neq("source", "qb_import");
    return out as T;
  };

  // Inner-join customers when filtering by customer name so the filter
  // narrows parent invoices (not just the embedded resource).
  const customerSel = customer ? "customers!inner(name)" : "customers(name)";
  const selectStr =
    `id, invoice_number, invoice_date, total, status, approved_by, approved_at, sent_at, paid_at, payment_reference, payment_method, due_date, amount_paid, notes, created_at, project_id, projects(name), customer_id, ${customerSel}, source, needs_customer_assignment, needs_project_assignment`;

  return useQuery({
    queryKey: [
      "invoices",
      statusFilter,
      user?.id,
      pagination?.page,
      trimmedSearch,
      customer,
      amountMin ?? null,
      amountMax ?? null,
      dateFrom ?? null,
      dateTo ?? null,
      invoiceNumber,
      memo,
      date ?? null,
      type ?? null,
    ],
    queryFn: async () => {
      if (pagination) {
        const countSel = customer ? "id, customers!inner(name)" : "id";
        let countQuery: any = supabase
          .from("invoices")
          .select(countSel, { count: "exact", head: true })
          .is("deleted_at", null);
        countQuery = applyFilters(countQuery);
        const { count } = await countQuery;
        pagination.setTotalCount(count ?? 0);
      }

      let query: any = supabase
        .from("invoices")
        .select(selectStr)
        .is("deleted_at", null)
        .order("invoice_date", { ascending: false })
        .order("created_at", { ascending: false });

      query = applyFilters(query);

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
    placeholderData: keepPreviousData,
  });
};
